// Канонические идентификаторы продуктовых модулей StudyHub.
//
// Правило принадлежности (ТЗ §4.4): `ModuleId` получает модуль, для которого
// ответ на вопрос «может ли этого не быть в продукте?» — да. Инфраструктурные
// модули (auth, billing, sync, quotas, search, learning, recommendations…)
// идентификатора НЕ имеют: их нельзя выключить, а часть из них — потребители
// реестра, а не его участники.
//
// Список экспортируется И как union-тип, И как рантайм-массив: coverage-тесты
// Ш1 итерируют по `MODULE_IDS`, а не по «списку, который есть только в типах».
// Отсутствие рантайм-списка — известная боль `AI_PROVIDER_ROUTING` (48 useCase
// без экспортируемого перечисления), повторять её нельзя.

import { z } from "zod";

/**
 * Внутренние модули продукта — 20 штук (ТЗ §4.4).
 *
 * Порядок — по группам `kind`, чтобы список читался как карта продукта:
 * форматы обучения → захват → самостоятельные инструменты → платформенные →
 * мета → производные → жизненный цикл.
 *
 * Заметки о неочевидных id:
 * - `voice-tutor` — код живёт подмодулем в `modules/tutor/voice/`, но это
 *   отдельный `ModuleId`: его выключение не должно гасить текстового репетитора.
 * - `courses` — курс реализован поверх `study_plans`; `study-plan` собственного
 *   id не получает (движок, не модуль), его вклады декларирует `courses`.
 * - `library` — коллекции (`material_sets`), исторический алиас `collections`.
 */
export const INTERNAL_MODULE_IDS = [
	// learning-format
	"notes",
	"quizzes",
	"flashcards",
	"glossary",
	"audio-recap",
	"voice-tutor",
	// capture
	"recordings",
	"discovery",
	// standalone-tool
	"essays",
	"visual-explainer",
	// platform-capability
	"tutor",
	// meta
	"materials",
	"library",
	"courses",
	"calendar",
	"streaks",
	"course-templates",
	// derivative
	"progress",
	"weak-spots",
	// lifecycle
	"trash",
] as const;

/**
 * Внешние (федеративные) модули — сервисы за `ExternalModulePort`.
 *
 * `characters` (CharacterHub) заводится ЗАРАНЕЕ, на Ш1, хотя реализуется в Ш7.
 * Причина простая: платформа строится ради того, чтобы модуль #39 не стал
 * исключением в 13 реестрах. Если внешний id появится только вместе с самим
 * модулем, все coverage-тесты и типы будут написаны в предположении «внешних
 * нет» — и Ш7 снова окажется ручной интеграцией.
 *
 * ВНИМАНИЕ для coverage-тестов: до Ш7 у `characters` нет ни серверного модуля,
 * ни манифеста. Тесты, сканирующие `apps/server/src/modules/`, обязаны итерировать
 * `INTERNAL_MODULE_IDS`, а не `MODULE_IDS`.
 */
export const EXTERNAL_MODULE_IDS = ["characters"] as const;

/** Полный union идентификаторов (внутренние + внешние). */
export const MODULE_IDS = [...INTERNAL_MODULE_IDS, ...EXTERNAL_MODULE_IDS] as const;

export type InternalModuleId = (typeof INTERNAL_MODULE_IDS)[number];
export type ExternalModuleId = (typeof EXTERNAL_MODULE_IDS)[number];
export type ModuleId = (typeof MODULE_IDS)[number];

/** Zod-схема id — для парсинга недоверенного входа: deep-link, URL, `admin_settings` JSONB. */
export const moduleIdSchema = z.enum(MODULE_IDS);

/** true, если строка — известный `ModuleId` (без учёта алиасов). */
export function isModuleId(value: string): value is ModuleId {
	return (MODULE_IDS as readonly string[]).includes(value);
}

/** true, если id принадлежит внешнему (федеративному) модулю. */
export function isExternalModuleId(value: ModuleId): value is ExternalModuleId {
	return (EXTERNAL_MODULE_IDS as readonly string[]).includes(value);
}

/**
 * Легаси-имена → канонический id.
 *
 * Живут не в документации, а в коде, потому что по этим строкам приходят
 * реальные данные: сохранённые deep-links, письма, `recommendations.deepLink`,
 * URL-редиректы (`/study-plan/*` → `/courses`), старые записи аналитики.
 * Любой резолвер (Route Guard, Cmd-K, deep-link) обязан прогонять вход через
 * `normalizeModuleId`, иначе легаси-ссылка молча даст «модуль не найден».
 *
 * Алиасы НЕ являются частью `ModuleId` — писать их в манифест запрещено.
 */
export const MODULE_ID_ALIASES = {
	// Исторические имена модуля тестов: и `tests` (роут/раздел), и `quiz` (артефакт).
	tests: "quizzes",
	quiz: "quizzes",
	// Историческое имя модуля аудио-резюме: артефакт, таблица, маршрут и
	// socket-события всегда были `audio-recap`, а модуль и контракт — нет.
	"audio-summary": "audio-recap",
	// Движок `study_plans` был отдельным разделом до Course Workspace V2.
	"study-plan": "courses",
	// Коллекции материалов переименованы в Библиотеку.
	collections: "library",
	// Каталог шаблонов курсов.
	templates: "course-templates",
} as const satisfies Record<string, ModuleId>;

export type ModuleIdAlias = keyof typeof MODULE_ID_ALIASES;

/**
 * Приводит произвольную строку к каноническому `ModuleId`.
 *
 * @returns `ModuleId` — если это канонический id либо известный алиас;
 *          `null` — если модуль неизвестен (потребитель обязан обработать
 *          это как «удалённый модуль», см. `REMOVED_MODULE_AVAILABILITY`).
 */
export function normalizeModuleId(raw: string): ModuleId | null {
	const value = raw.trim().toLowerCase();
	if (isModuleId(value)) return value;
	const alias = (MODULE_ID_ALIASES as Record<string, ModuleId | undefined>)[value];
	return alias ?? null;
}

/** Алиас-имя канонического id (обратный поиск) — для генерации редиректов. */
export function aliasesOf(id: ModuleId): ModuleIdAlias[] {
	return Object.entries(MODULE_ID_ALIASES)
		.filter(([, canonical]) => canonical === id)
		.map(([alias]) => alias as ModuleIdAlias);
}
